import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithRedirect, 
  getRedirectResult,
  signInWithPopup 
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, updateDoc } from 'firebase/firestore';

import { firebase } from '../../../services/firebase';
import { logEvent } from '../../../utils/analytics';
import { useLoader } from '../../../contexts/LoaderContext';

import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import moment from 'moment';

import { 
  LOGIN_TITLE, 
  LOGIN_SUBTITLE, 
  GOOGLE_BUTTON_LABEL,
  ANALYTICS_EVENTS,
  ERROR_MESSAGES,
  USER_DEFAULTS,
  PATHS
} from './constant';

import logo from '../../../assets/logo_transparent.png';
import './style.css';
import { checkAccess } from '../../../utils/accessControl';

const Login = () => {
  const navigate = useNavigate();
  const auth = getAuth();
  const { showLoader, hideLoader } = useLoader();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  

  useEffect(() => {
    let isMounted = true;

    const handleRedirect = async () => {
      try {
        setError(null);
        showLoader({ title: 'Signing you in…', subText: 'Please wait' });
        const result = await getRedirectResult(auth);
        if (!result || !result.user) return; // nothing to process

        const user = result.user;
        const userRef = doc(firebase, 'users', user.uid);
        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) {
          await setDoc(userRef, {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            createdAt: moment(serverTimestamp()).unix(),
            lastLogin: moment(serverTimestamp()).unix(),
            setupCompleted: USER_DEFAULTS.SETUP_COMPLETED
          });
        } else {
          await setDoc(userRef, { lastLogin: moment(serverTimestamp()).unix() }, { merge: true });
        }

        logEvent(ANALYTICS_EVENTS.LOGIN, { method: 'google', user_id: user.uid });
        // Do not navigate here; let useSessionMonitor route appropriately
      } catch (err) {
        if (!isMounted) return;
        console.error(ERROR_MESSAGES.SIGN_IN, err);
        setError(err.message);
        logEvent(ANALYTICS_EVENTS.LOGIN_ERROR, { error: err.message, method: 'google' });
      } finally {
        hideLoader();
      }
    };

    handleRedirect();
    return () => { isMounted = false; };
  }, [auth, hideLoader, navigate, showLoader]);

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      showLoader({ title: 'Signing in...' });
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      const user = result.user;
      const userDoc = await getDoc(doc(firebase, 'users', user.uid));
      
      if (!userDoc.exists()) {
        // New user setup
        await setDoc(doc(firebase, 'users', user.uid), {
          email: user.email,
          displayName: user.displayName || user.email.split('@')[0],
          photoURL: user.photoURL || '',
          createdAt: moment(serverTimestamp()).unix(),
          setupCompleted: false,
        });
        // Redirect to first-time setup
        navigate(PATHS.FIRST_TIME_SETUP);
      } else {
        // Existing user - update last login
        await updateDoc(doc(firebase, 'users', user.uid), {
          lastLogin: moment(serverTimestamp()).unix()
        });
        // Check if setup was completed
        const userData = userDoc.data();
        if (!userData.setupCompleted) {
          navigate(PATHS.FIRST_TIME_SETUP);
        } else {
          navigate(PATHS.DASHBOARD);
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(ERROR_MESSAGES.GENERAL_LOGIN_ERROR);
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const header = (
    <div className="text-center">
      <img alt="Yes Boss" src={logo} className="w-6rem mb-3" />
    </div>
  );

  const footer = (
    <div className="text-center">
      <small className="text-600">
        By continuing, you agree to our Terms of Service and Privacy Policy
      </small>
    </div>
  );

  return (
    <div className="login-container">
      <Card className="login-card" header={header} footer={footer}>
        <div className="text-center mb-5">
          <h1 className="text-3xl font-medium mb-1">{LOGIN_TITLE}</h1>
          <p className="text-600 mt-0">{LOGIN_SUBTITLE}</p>
        </div>

        {error && (
          <Message severity="error" text={error} className="mb-4" />
        )}

        <Button
          label={GOOGLE_BUTTON_LABEL}
          icon="pi pi-google"
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="w-full p-button-outlined"
        />
      </Card>
    </div>
  );
};

export default Login;
